import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../app/api";

type ScanVote = "ja" | "nein" | "enthaltung";
type JsQrDecoder = (
  data: Uint8ClampedArray,
  width: number,
  height: number
) => { data?: string } | null;

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M8.5 7.5 10 5h4l1.5 2.5H18a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9.5a2 2 0 0 1 2-2h2.5Z" />
    <path d="M12 10.25a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
    <path d="M17.25 10h.01" />
  </svg>
);

const QrCodeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M5 5h5v5H5z" />
    <path d="M14 5h5v5h-5z" />
    <path d="M5 14h5v5H5z" />
    <path d="M14 14h2.5v2.5H14z" />
    <path d="M18.5 14v2.5" />
    <path d="M14 19h2.5" />
    <path d="M19 19h.01" />
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 16V5" />
    <path d="m7.5 9.5 4.5-4.5 4.5 4.5" />
    <path d="M6 15.5a4 4 0 0 0 1 7.5h10a4 4 0 0 0 1-7.5" />
  </svg>
);

const ScanFrameIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M8 4H5a1 1 0 0 0-1 1v3" />
    <path d="M16 4h3a1 1 0 0 1 1 1v3" />
    <path d="M20 16v3a1 1 0 0 1-1 1h-3" />
    <path d="M8 20H5a1 1 0 0 1-1-1v-3" />
    <path d="M9 12h6" />
  </svg>
);

const unknownQrCodeMessage =
  "Der gescannte QR-Code ist nicht bekannt. Bitte prüfen Sie, ob der Code zu einem analogen Rückläufer dieses Mandanten gehört.";

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(d);
};

const readTokenFromScanText = (value: string) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const extractTokenCandidate = (input: string) => {
    const cleaned = String(input ?? "").replace(/[),;]+$/g, "").trim();
    if (!cleaned) return "";

    const decoded = (() => {
      try {
        return decodeURIComponent(cleaned);
      } catch {
        return cleaned;
      }
    })();

    const fromQuery =
      decoded.match(/[?&](?:token|t)=([^&#\s]+)/i)?.[1] ??
      cleaned.match(/[?&](?:token|t)=([^&#\s]+)/i)?.[1] ??
      "";
    if (fromQuery) return fromQuery.trim();

    const fromAnalogPath =
      decoded.match(/(?:^|\/)analog-scan\/([^/?#\s]+)/i)?.[1] ??
      cleaned.match(/(?:^|\/)analog-scan\/([^/?#\s]+)/i)?.[1] ??
      "";
    if (fromAnalogPath) return fromAnalogPath.trim();

    const fromOwnerPath =
      decoded.match(/(?:^|\/)owner-vote\/([^/?#\s]+)/i)?.[1] ??
      cleaned.match(/(?:^|\/)owner-vote\/([^/?#\s]+)/i)?.[1] ??
      "";
    if (fromOwnerPath) return fromOwnerPath.trim();

    return decoded.trim();
  };

  try {
    const asUrl = new URL(raw);
    const fromQuery = (asUrl.searchParams.get("token") ?? asUrl.searchParams.get("t") ?? "").trim();
    if (fromQuery) return fromQuery;
    const parts = asUrl.pathname.split("/").filter(Boolean);
    const analogIndex = parts.findIndex((p) => p === "analog-scan");
    if (analogIndex >= 0 && parts[analogIndex + 1]) return parts[analogIndex + 1];
    const ownerVoteIndex = parts.findIndex((p) => p === "owner-vote");
    if (ownerVoteIndex >= 0 && parts[ownerVoteIndex + 1]) return parts[ownerVoteIndex + 1];
    return extractTokenCandidate(raw);
  } catch {
    return extractTokenCandidate(raw);
  }
};

export const AnalogScanPage = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token?: string }>();
  const [voteValue, setVoteValue] = useState<ScanVote>("ja");
  const [comment, setComment] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [scannerError, setScannerError] = useState("");
  const [scannerRunning, setScannerRunning] = useState(false);
  const [scannerMode, setScannerMode] = useState<"native" | "compat" | null>(null);
  const [imageScanRunning, setImageScanRunning] = useState(false);
  const [uploadDragging, setUploadDragging] = useState(false);
  const [supportsScanner, setSupportsScanner] = useState(false);
  const scannerTimerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const jsQrRef = useRef<JsQrDecoder | null>(null);
  const scanNavigationPendingRef = useRef(false);
  const unknownQrPauseUntilRef = useRef(0);

  const effectiveToken = useMemo(() => String(token ?? "").trim(), [token]);

  useEffect(() => {
    const hasCamera =
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia;
    setSupportsScanner(hasCamera);
  }, []);

  const stopScanner = () => {
    setScannerRunning(false);
    setScannerMode(null);
    setScannerError("");
    unknownQrPauseUntilRef.current = 0;
    if (scannerTimerRef.current) {
      window.clearInterval(scannerTimerRef.current);
      scannerTimerRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopScanner();
  }, []);

  const openKnownScanToken = async (parsedToken: string) => {
    const cleanToken = String(parsedToken ?? "").trim();
    if (!cleanToken || scanNavigationPendingRef.current) return false;
    scanNavigationPendingRef.current = true;
    try {
      await api.analogScanAccess(cleanToken);
      setScannerError("");
      stopScanner();
      navigate(`/analog-scan/${encodeURIComponent(cleanToken)}`);
      return true;
    } catch {
      setScannerError(unknownQrCodeMessage);
      unknownQrPauseUntilRef.current = Date.now() + 3000;
      navigate("/analog-scan", { replace: true });
      return false;
    } finally {
      scanNavigationPendingRef.current = false;
    }
  };

  const startScanner = async () => {
    if (!supportsScanner) return;
    setScannerError("");
    setSavedMessage("");
    stopScanner();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false
      });
      streamRef.current = stream;
      const videoEl = videoRef.current;
      if (!videoEl) {
        stopScanner();
        return;
      }
      videoEl.srcObject = stream;
      await videoEl.play();
      setScannerRunning(true);

      type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => {
        detect: (source: CanvasImageSource) => Promise<Array<{ rawValue?: string }>>;
      };
      const DetectorCtor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
      if (DetectorCtor) {
        setScannerMode("native");
        const detector = new DetectorCtor({ formats: ["qr_code"] });
        scannerTimerRef.current = window.setInterval(async () => {
          try {
            if (!videoRef.current) return;
            const codes = await detector.detect(videoRef.current);
            const firstRaw = String(codes?.[0]?.rawValue ?? "").trim();
            if (!firstRaw) return;
            if (Date.now() < unknownQrPauseUntilRef.current) return;
            const parsedToken = readTokenFromScanText(firstRaw);
            if (!parsedToken) return;
            await openKnownScanToken(parsedToken);
          } catch {
            // temporary decode errors are expected while camera adjusts
          }
        }, 600);
        return;
      }

      setScannerMode("compat");
      if (!jsQrRef.current) {
        const jsQrModule = await import("jsqr");
        jsQrRef.current = (jsQrModule.default ?? null) as JsQrDecoder | null;
      }
      if (!jsQrRef.current) throw new Error("Kompatibilitäts-Scanner konnte nicht geladen werden.");

      scannerTimerRef.current = window.setInterval(() => {
        try {
          const video = videoRef.current;
          if (!video || video.videoWidth <= 1 || video.videoHeight <= 1) return;
          const canvas = canvasRef.current ?? document.createElement("canvas");
          canvasRef.current = canvas;
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (!ctx) return;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const result = jsQrRef.current?.(imageData.data, imageData.width, imageData.height);
          const parsedToken = readTokenFromScanText(String(result?.data ?? ""));
          if (!parsedToken) return;
          if (Date.now() < unknownQrPauseUntilRef.current) return;
          void openKnownScanToken(parsedToken);
        } catch {
          // temporary decode errors are expected while camera adjusts
        }
      }, 500);
    } catch (error) {
      stopScanner();
      setScannerError(error instanceof Error ? error.message : "Kamera konnte nicht gestartet werden.");
    }
  };

  const accessQuery = useQuery({
    queryKey: ["analog-scan-access", effectiveToken],
    enabled: Boolean(effectiveToken),
    queryFn: () => api.analogScanAccess(effectiveToken),
    retry: false
  });

  useEffect(() => {
    if (!effectiveToken || !accessQuery.isError) return;
    setScannerError(unknownQrCodeMessage);
    navigate("/analog-scan", { replace: true });
  }, [accessQuery.isError, effectiveToken, navigate]);

  const hasExistingVote =
    Boolean(accessQuery.data?.current_vote) && String(accessQuery.data?.current_vote?.voteValue ?? "offen") !== "offen";

  const submitMutation = useMutation({
    mutationFn: () =>
      api.analogScanSubmit(effectiveToken, {
        voteValue,
        comment: comment.trim() ? comment.trim() : undefined
      }),
    onSuccess: async () => {
      setSavedMessage(
        hasExistingVote
          ? "Stimmänderung wurde erfolgreich erfasst."
          : "Stimme wurde erfolgreich erfasst."
      );
      await accessQuery.refetch();
    },
    onError: (error) => {
      setSavedMessage(error instanceof Error ? error.message : "Stimme konnte nicht gespeichert werden.");
    }
  });

  const scanQrFromImage = async (file: File) => {
    setScannerError("");
    setSavedMessage("");
    setImageScanRunning(true);
    try {
      type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => {
        detect: (source: CanvasImageSource) => Promise<Array<{ rawValue?: string }>>;
      };
      const DetectorCtor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
      const imageBitmap = await createImageBitmap(file);

      let parsedToken = "";
      if (DetectorCtor) {
        const detector = new DetectorCtor({ formats: ["qr_code"] });
        const codes = await detector.detect(imageBitmap);
        parsedToken = readTokenFromScanText(String(codes?.[0]?.rawValue ?? ""));
      }

      if (!parsedToken) {
        if (!jsQrRef.current) {
          const jsQrModule = await import("jsqr");
          jsQrRef.current = (jsQrModule.default ?? null) as JsQrDecoder | null;
        }
        if (!jsQrRef.current) {
          throw new Error("Bild-Scan wird in diesem Browser nicht unterstützt.");
        }
        const canvas = canvasRef.current ?? document.createElement("canvas");
        canvasRef.current = canvas;
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) throw new Error("Bild konnte nicht verarbeitet werden.");
        ctx.drawImage(imageBitmap, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const result = jsQrRef.current(imageData.data, imageData.width, imageData.height);
        parsedToken = readTokenFromScanText(String(result?.data ?? ""));
      }

      if (!parsedToken) throw new Error("Kein QR-Code erkannt. Bitte Foto erneut aufnehmen.");
      await openKnownScanToken(parsedToken);
    } catch (error) {
      setScannerError(error instanceof Error ? error.message : "QR-Bild konnte nicht ausgewertet werden.");
    } finally {
      setImageScanRunning(false);
    }
  };

  return (
    <div
      className={`owner-vote-shell analog-scan-shell${scannerRunning ? " analog-scan-shell--scanner-running" : ""}`}
    >
      <section className="owner-vote-card analog-scan-card">
        <header className="analog-scan-v2-header">
          <span className="analog-scan-v2-title-bullet">
            <QrCodeIcon />
          </span>
          <div>
            <h1 className="analog-scan-v2-title">Analoge Rückläufer erfassen (QR)</h1>
            <p className="analog-scan-v2-subline">
              QR-Code vom analogen Umlaufbeschluss scannen.
            </p>
          </div>
        </header>

        {!effectiveToken ? (
          <div className="analog-scan-v2-panel">
            <section className="analog-scan-v2-row">
              <span className="analog-scan-v2-row-icon">
                <CameraIcon />
              </span>
              <div className="analog-scan-v2-row-copy">
                <div className="analog-scan-v2-row-main">
                  <strong>Kamera-Scan</strong>
                  <p>QR-Code mit der Kamera scannen.</p>
                </div>
                <div className="analog-scan-v2-badge-row">
                  {!supportsScanner ? <span className="analog-scan-v2-badge is-unavailable">Scanner im Browser nicht verfügbar</span> : null}
                  {scannerRunning && scannerMode === "compat" ? <span className="analog-scan-v2-badge is-active">Kompatibilitätsmodus (iOS)</span> : null}
                </div>
              </div>
              <div className="analog-scan-v2-row-action">
                {!scannerRunning ? (
                  <button
                    type="button"
                    className="analog-scan-v2-primary-btn"
                    disabled={!supportsScanner}
                    onClick={() => void startScanner()}
                  >
                    <ScanFrameIcon />
                    Scanner starten
                  </button>
                ) : (
                  <button type="button" className="analog-scan-v2-secondary-btn" onClick={stopScanner}>
                    Scanner stoppen
                  </button>
                )}
              </div>
              <video
                ref={videoRef}
                className="analog-scan-v2-video"
                style={{ display: scannerRunning ? "block" : "none" }}
                playsInline
                muted
              />
              {scannerError ? <div className="error-text analog-scan-v2-message">{scannerError}</div> : null}
            </section>

            <section className="analog-scan-v2-row">
              <span className="analog-scan-v2-row-icon">
                <UploadIcon />
              </span>
              <div className="analog-scan-v2-row-copy">
                <div className="analog-scan-v2-row-main">
                  <strong>Foto-Upload</strong>
                  <p>
                    Für Browser ohne Live-Scanner (z. B. iPhone Safari):
                    <br />
                    QR fotografieren und Bild hier auswählen.
                  </p>
                </div>
                <div className="analog-scan-v2-upload-wrap">
                  <label className="analog-scan-v2-file-btn">
                    <UploadIcon />
                    Datei auswählen
                    <input
                      type="file"
                      className="analog-scan-file-input analog-scan-v2-file-native"
                      accept="image/*"
                      capture="environment"
                      disabled={imageScanRunning}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void scanQrFromImage(file);
                        e.currentTarget.value = "";
                      }}
                    />
                  </label>
                  <span className="analog-scan-v2-file-state">Keine ausgewählt</span>
                </div>
                {imageScanRunning ? <span className="analog-scan-v2-badge is-active">Bild wird ausgewertet...</span> : null}
              </div>
              <div
                className={`analog-scan-v2-dropzone${uploadDragging ? " is-dragging" : ""}${imageScanRunning ? " is-disabled" : ""}`}
                role="button"
                tabIndex={0}
                aria-label="QR-Bild per Drag and Drop hochladen"
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!imageScanRunning) setUploadDragging(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!imageScanRunning) setUploadDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                  setUploadDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setUploadDragging(false);
                  if (imageScanRunning) return;
                  const file = Array.from(e.dataTransfer.files).find((item) => item.type.startsWith("image/"));
                  if (file) void scanQrFromImage(file);
                }}
              >
                <UploadIcon />
                <span>Bild hier ablegen</span>
              </div>
            </section>
          </div>
        ) : null}

        {effectiveToken ? (
          <div className="analog-scan-grid">
            {accessQuery.isError ? (
              <div className="error-text">
                {accessQuery.error instanceof Error ? accessQuery.error.message : "Scan-Token konnte nicht geladen werden."}
              </div>
            ) : null}

            {accessQuery.data ? (
              <>
                <section className="owner-vote-action-box analog-scan-box">
                  <div style={{ display: "grid", gap: 6 }}>
                    <div>
                      <strong>Beschluss:</strong> {accessQuery.data.resolution.beschlussnummer} | {accessQuery.data.resolution.titel}
                    </div>
                    <div>
                      <strong>Objekt:</strong> {accessQuery.data.resolution.objektnummer} | {accessQuery.data.resolution.objektname}
                    </div>
                    <div>
                      <strong>Eigentümer:</strong> {accessQuery.data.owner.name}
                    </div>
                    <div>
                      <strong>Einheiten:</strong>{" "}
                      {accessQuery.data.assignments.map((row) => row.einheitsnummer.padStart(4, "0")).join(" | ") || "-"}
                    </div>
                    <div>
                      <strong>Aktuelle Stimme:</strong>{" "}
                      {accessQuery.data.current_vote ? accessQuery.data.current_vote.voteValue : "offen"}
                      {accessQuery.data.current_vote?.voteReceivedAt ? ` (erfasst am ${formatDateTime(accessQuery.data.current_vote.voteReceivedAt)})` : ""}
                    </div>
                  </div>
                </section>

                <section className="owner-vote-action-box analog-scan-box">
                  {hasExistingVote ? (
                    <div className="analog-scan-change-hint">
                      Für diesen Rückläufer wurde bereits eine Stimme erfasst. Wenn Sie jetzt speichern, wird eine <strong>Stimmänderung</strong> protokolliert.
                    </div>
                  ) : null}

                  <div className="form-field">
                    <label>Stimmabgabe</label>
                    <div className="owner-vote-options analog-scan-options">
                      <button
                        type="button"
                        className={`owner-vote-option owner-vote-option-ja${voteValue === "ja" ? " is-active" : ""}`}
                        onClick={() => setVoteValue("ja")}
                      >
                        Ja
                      </button>
                      <button
                        type="button"
                        className={`owner-vote-option owner-vote-option-nein${voteValue === "nein" ? " is-active" : ""}`}
                        onClick={() => setVoteValue("nein")}
                      >
                        Nein
                      </button>
                      <button
                        type="button"
                        className={`owner-vote-option owner-vote-option-enthaltung${voteValue === "enthaltung" ? " is-active" : ""}`}
                        onClick={() => setVoteValue("enthaltung")}
                      >
                        Enthaltung
                      </button>
                    </div>
                  </div>
                  <div className="form-field owner-vote-comment-field">
                    <label>Kommentar (optional)</label>
                    <textarea className="table-field" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} />
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    <button
                      type="button"
                      className={`owner-vote-submit-btn ${
                        voteValue === "ja"
                          ? "owner-vote-submit-ja"
                          : voteValue === "nein"
                          ? "owner-vote-submit-nein"
                          : "owner-vote-submit-enthaltung"
                      }`}
                      disabled={submitMutation.isPending}
                      onClick={() => submitMutation.mutate()}
                    >
                      {submitMutation.isPending ? "Speichere..." : hasExistingVote ? "Stimmänderung speichern" : "Stimme speichern"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/analog-scan");
                        setSavedMessage("");
                      }}
                    >
                      Neuer Scan
                    </button>
                  </div>
                  {savedMessage ? (
                    <div
                      style={{ marginTop: 10 }}
                      className={savedMessage.toLowerCase().includes("erfolgreich") ? "success-text" : "error-text"}
                    >
                      {savedMessage}
                    </div>
                  ) : null}
                </section>
              </>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
};
